import { useEffect } from "react";
import { siteConfig } from "@/lib/site-config";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  ogType?: "website" | "article";
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Dependency-free head manager.
 *
 * The template shipped `react-helmet-async` pinned to a version that does not
 * exist on npm, so in the production build Helmet injected nothing and every
 * route inherited index.html's static title/description with no canonical.
 * This imperatively upserts the per-page SEO tags in a `useEffect` instead, so
 * each route gets a real <title>, description, canonical and social tags.
 * The props are unchanged, so no page needed editing.
 */

const upsertMeta = (selector: string, attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const Seo = ({ title, description, path, ogType = "website", ogImage, jsonLd }: SeoProps) => {
  const url = `${siteConfig.url}${path}`;
  const image = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : `${siteConfig.url}${ogImage}`
    : `${siteConfig.url}${siteConfig.ogImage}`;

  useEffect(() => {
    document.title = title;

    upsertMeta('meta[name="description"]', "name", "description", description);
    upsertLink("canonical", url);

    upsertMeta('meta[property="og:type"]', "property", "og:type", ogType);
    upsertMeta('meta[property="og:title"]', "property", "og:title", title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:image"]', "property", "og:image", image);

    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", image);

    // Replace any per-page JSON-LD from a previous render, then append this page's.
    const previous = document.head.querySelectorAll('script[data-seo-jsonld="true"]');
    previous.forEach((n) => n.remove());
    const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    const added: HTMLScriptElement[] = [];
    ldArray.forEach((ld) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.textContent = JSON.stringify(ld);
      document.head.appendChild(s);
      added.push(s);
    });

    return () => {
      added.forEach((n) => n.remove());
    };
  }, [title, description, url, ogType, image, jsonLd]);

  return null;
};

export default Seo;
